import { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { type ClickType } from '@jsmdg/tracking';
import { OverlayLoader } from '@jsmdg/yoshi';
import { type CartError } from '../../errors/cartError';
import { usePayPalExpressBuybox } from '../../hooks/usePayPalExpressBuybox';
import styles from './PayPalBuyboxButton.module.scss';

type PayPalBuyboxButtonProps = {
    readonly productId: string;
    readonly quantity: number;
    readonly buttonHeight: number;
    readonly clickType: ClickType;
    readonly disableButton: boolean;
    readonly onAddToCartError: (errorCode?: number) => void;
    readonly handleError: (error: CartError) => void;
};

const messages = defineMessages({
    a11yLoadingText: {
        defaultMessage: 'Bitte warten',
    },
});

const PAYPAL_SDK_BUTTON_HEIGHT = 48;

const PayPalBuyboxButton = ({
    buttonHeight,
    clickType,
    disableButton,
    handleError,
    onAddToCartError,
    productId,
    quantity,
}: PayPalBuyboxButtonProps): JSX.Element => {
    const intl = useIntl();
    const effectiveButtonHeight = buttonHeight > 0 ? buttonHeight : PAYPAL_SDK_BUTTON_HEIGHT;
    const { isLoading: isPayPalLoading, setPayPalButtonWrapperElement } = usePayPalExpressBuybox({
        buttonHeight: PAYPAL_SDK_BUTTON_HEIGHT,
        clickType,
        handleError,
        onAddToCartError,
        productId,
        quantity,
    });

    const setPaypalButtonWrapperRef = useCallback(
        (element: HTMLDivElement | null) => {
            setPayPalButtonWrapperElement(element ?? undefined);
        },
        [setPayPalButtonWrapperElement],
    );

    return (
        <>
            {isPayPalLoading && (
                <OverlayLoader
                    a11yText={intl.formatMessage(messages.a11yLoadingText)}
                    data-testid="loader-element"
                >
                    <FormattedMessage defaultMessage="Bitte warten..." />
                </OverlayLoader>
            )}
            <div
                className={classNames('d-flex position-relative', styles.paypalButtonWrapper, {
                    [styles.disabledOverlay]: disableButton,
                })}
                style={{ height: effectiveButtonHeight }}
                ref={setPaypalButtonWrapperRef}
                data-testid="buy-with-paypal-button"
            />
        </>
    );
};

export { PayPalBuyboxButton };
